"use client";

import { useChatboxStore } from "../../store";
import { type EmojiElementProps, EmojiPicker } from "./EmojiPicker";
import { MessagingMessageType } from "@/model/messaging";
import { cn } from "@deputy-ai/common";
import { Button } from "@deputy-ai/ui/button/Button";
import { Popover } from "@deputy-ai/ui/popover/Popover";
import { Separator } from "@deputy-ai/ui/separator/Separator";
import { Extension } from "@tiptap/core";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  type DetailedHTMLProps,
  forwardRef,
  type HTMLAttributes,
  type MouseEvent,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  BiBold,
  BiCollapseAlt,
  BiExpandAlt,
  BiItalic,
  BiSolidSend,
  BiStrikethrough,
} from "react-icons/bi";
import { GoPlusCircle } from "react-icons/go";
import { IoImageOutline } from "react-icons/io5";
import { PiFileArrowUp } from "react-icons/pi";

export interface MessageInputHandler {
  setContent?: (content: string) => void;
}

export const MessageInput = forwardRef<
  MessageInputHandler,
  DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>
>(({ className }, ref) => {
  const {
    use: { inputDisabled, canSendMessage, options },
    bind: { sendMessage, sendFile },
  } = useChatboxStore();
  const { compact = true, richText } = options?.input ?? {};
  const [showGrowingPanel, setShowGrowingPanel] = useState(false);

  const [buttonVariant, setButtonVariant] = useState({
    bold: false,
    italic: false,
    strike: false,
  });

  const inputRef = useRef<HTMLDivElement | null>(null);
  const [showInputMb, setShowInputMb] = useState(false);

  const growing = useRef(showGrowingPanel);

  useEffect(() => {
    growing.current = showGrowingPanel;
  }, [showGrowingPanel]);

  const sendRef = useRef<(e?: MouseEvent) => void>(null);
  const getEditorContent = () => {
    if (!editor) {
      return "";
    }

    if (!richText) {
      return editor.getText();
    }

    const paragraphPattern = /<p>(.*)<\/p>/;
    const htmlContent = editor.getHTML();
    const groups = paragraphPattern.exec(htmlContent);

    if (!groups || groups.length < 2 || !groups[1]) {
      return "";
    } else {
      return groups[1];
    }
  };

  const handleSend = async (e?: MouseEvent) => {
    e?.stopPropagation();
    const content = getEditorContent();
    if (!editor || !content || !canSendMessage) {
      return;
    }
    editor.commands.clearContent();
    await sendMessage(content);
    editor.commands.focus();
    setShowGrowingPanel(false);
    setShowInputMb(false);
  };

  sendRef.current = handleSend;

  const shiftEnterRef = useRef<Extension>(null);

  shiftEnterRef.current ??= Extension.create({
    addKeyboardShortcuts() {
      return {
        Enter: ({ editor }) => {
          if (growing.current) {
            editor.commands.keyboardShortcut("Mod-Enter");
            return true;
          }
          sendRef.current?.();
          return true;
        },
        "Shift-Enter": () => {
          sendRef.current?.();
          return true;
        },
      };
    },
  });

  const editor = useEditor({
    editorProps: {
      attributes: {
        class: "h-full w-full outline-hidden text-gray-700 px-3",
      },
    },
    onUpdate({ editor }) {
      if (!editor.getText()) {
        setShowInputMb(false);
      }
    },
    immediatelyRender: false,
    extensions: [StarterKit, shiftEnterRef.current],
  });

  useImperativeHandle(ref, () => ({
    setContent: (content) => editor?.commands.setContent(content),
  }));

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      const clientHeight = entries[0]?.target.clientHeight;
      const element = entries[0]?.target;

      if (!element || !clientHeight) {
        return;
      }

      const style = window.getComputedStyle(element);

      const fontSize = parseFloat(style.fontSize);
      const lineHeight = parseFloat(style.lineHeight);

      const computedLineHeight = isNaN(lineHeight)
        ? fontSize * 1.2
        : lineHeight;

      const paddingTop = parseFloat(style.paddingTop);
      const paddingBottom = parseFloat(style.paddingBottom);
      const borderTop = parseFloat(style.borderTopWidth);
      const borderBottom = parseFloat(style.borderBottomWidth);

      const singleLineHeight =
        computedLineHeight +
        paddingTop +
        paddingBottom +
        borderTop +
        borderBottom;

      setShowInputMb((prev) => prev || clientHeight > singleLineHeight);
    });

    if (inputRef.current) {
      observer.observe(inputRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const uploadImageRef = useRef<HTMLInputElement | null>(null);
  const uploadFileRef = useRef<HTMLInputElement | null>(null);

  const EditorEl = <EditorContent editor={editor} />;

  useEffect(() => {
    editor?.setEditable(!inputDisabled);
    if (!inputDisabled) {
      editor?.commands?.focus("end");
    }
  }, [editor, inputDisabled]);

  useEffect(() => {
    if (options?.input?.autoFocus) {
      editor?.commands?.focus("end");
    }
  }, [options?.input?.autoFocus]);

  const renderGrowingInput = () => {
    if (!editor) {
      return null;
    }

    return (
      <div className="m-2 flex h-[25rem] flex-col rounded border p-2 transition-all">
        <div className="flex justify-between p-1">
          <div className="flex flex-row gap-1">
            <Button
              variant={buttonVariant.bold ? "secondary" : "ghost"}
              size="sm"
              icon={<BiBold />}
              onClick={() => {
                setButtonVariant((prevState) => ({
                  ...prevState,
                  bold: !prevState.bold,
                }));
                editor?.chain().focus().toggleBold().run();
              }}
            />
            <Button
              variant={buttonVariant.italic ? "secondary" : "ghost"}
              size="sm"
              icon={<BiItalic />}
              onClick={() => {
                setButtonVariant((prevState) => ({
                  ...prevState,
                  italic: !prevState.italic,
                }));
                editor?.chain().focus().toggleItalic().run();
              }}
            />
            <Button
              variant={buttonVariant.strike ? "secondary" : "ghost"}
              size="sm"
              icon={<BiStrikethrough />}
              onClick={() => {
                setButtonVariant((prevState) => ({
                  ...prevState,
                  strike: !prevState.strike,
                }));
                editor?.chain().focus().toggleStrike().run();
              }}
            />
            <EmojiPicker onPicker={onPickEmoji} />
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowGrowingPanel(false)}
            icon={<BiCollapseAlt />}
          />
        </div>

        <div className="h-full" onClick={() => editor?.commands.focus()}>
          {EditorEl}
        </div>
      </div>
    );
  };

  const onPickEmoji = (e: EmojiElementProps) => {
    editor?.commands.insertContent(e.native);
  };

  const renderImageUploader = () => {
    return (
      <Button
        variant="ghost"
        size="sm"
        className={cn(compact && "w-full justify-start")}
        disabled={!canSendMessage}
        icon={<IoImageOutline />}
        onClick={(e) => {
          e.stopPropagation();
          uploadImageRef.current?.click();
        }}
      >
        {compact && <span>Image</span>}
      </Button>
    );
  };

  const renderFileUploader = () => {
    return (
      <Button
        variant="ghost"
        size="sm"
        className={cn(compact && "w-full justify-start")}
        disabled={!canSendMessage}
        icon={<PiFileArrowUp />}
        onClick={(e) => {
          e.stopPropagation();
          uploadFileRef.current?.click();
        }}
      >
        {compact && <span>File</span>}
      </Button>
    );
  };

  return (
    <div
      className={cn("w-full bg-white", className)}
      style={{
        maxWidth: `${options?.maxWidth ?? 800}px`,
      }}
    >
      <input
        ref={uploadImageRef}
        className="hidden"
        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
        type="file"
        disabled={inputDisabled}
        onChange={(e) => {
          const file = e?.target?.files?.[0];
          e.target.value = "";
          if (file) {
            void sendFile({
              file,
              fileType: MessagingMessageType.Image,
            });
          }
        }}
      />
      <input
        ref={uploadFileRef}
        className="hidden"
        type="file"
        disabled={inputDisabled}
        onChange={(e) => {
          const file = e?.target?.files?.[0];
          e.target.value = "";
          if (file) {
            void sendFile({
              file,
              fileType: MessagingMessageType.File,
            });
          }
        }}
      />
      {showGrowingPanel ? (
        renderGrowingInput()
      ) : (
        <div
          onClick={() => editor?.commands?.focus()}
          className={cn(
            "relative m-2 flex items-center overflow-hidden rounded-lg border border-gray-200 p-1 focus-within:border-black",
            compact ? "flex-row" : "flex-col items-start p-2"
          )}
        >
          <div
            ref={inputRef}
            className={cn(
              "h-fit max-h-96 flex-1 overflow-y-auto py-1",
              showInputMb && "mb-8",
              !compact && "w-full"
            )}
          >
            {EditorEl}
          </div>
          <div
            className={cn(
              "flex flex-row items-center",
              showInputMb && "absolute right-1 bottom-1",
              !compact && "w-full justify-end"
            )}
          >
            <EmojiPicker onPicker={onPickEmoji} disabled={inputDisabled} />
            {compact ? (
              <Popover>
                <Popover.Trigger asChild>
                  <Button
                    className={cn(!canSendMessage && "cursor-not-allowed")}
                    variant="ghost"
                    size="sm"
                    disabled={!canSendMessage}
                    icon={<GoPlusCircle />}
                    onClick={(e) => e.stopPropagation()}
                  ></Button>
                </Popover.Trigger>
                <Popover.Content
                  align="end"
                  side="top"
                  className="flex w-fit flex-col gap-1 p-1"
                >
                  <Popover.Close asChild>{renderImageUploader()}</Popover.Close>
                  <Popover.Close asChild>{renderFileUploader()}</Popover.Close>
                </Popover.Content>
              </Popover>
            ) : (
              <>
                {renderImageUploader()}
                {renderFileUploader()}
                <Separator orientation="vertical" className="mx-1 h-4" />
              </>
            )}
            {richText && (
              <Button
                variant="ghost"
                size="sm"
                icon={<BiExpandAlt />}
                onClick={() => setShowGrowingPanel(true)}
              ></Button>
            )}
            <Button
              className={cn(!canSendMessage && "cursor-not-allowed")}
              variant="ghost"
              size="sm"
              onClick={handleSend}
              disabled={!canSendMessage}
              icon={<BiSolidSend />}
            ></Button>
          </div>
        </div>
      )}
    </div>
  );
});

MessageInput.displayName = "MessageInput";
